import { PATH_DASHBOARD } from '@/routes/paths';
import { copyToClipboard } from '@/utils/wechatDetection';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import { Box, Button, Divider, Paper, Stack, Typography } from '@mui/material';
import { useState } from 'react';

interface PublicFormLinkCardProps {
    formPath?: string;
    title?: string;
    description?: string;
}

export default function PublicFormLinkCard({
    formPath = PATH_DASHBOARD.public.posInquiry(),
    title = 'Share the merchant enquiry form',
    description = 'Send this link to restaurants that want to enquire about OrderBuddy POS.'
}: PublicFormLinkCardProps): JSX.Element {
    const [hasCopied, setHasCopied] = useState(false);

    const handleCopyLink = async (): Promise<void> => {
        const origin = window.location.origin;
        const success = await copyToClipboard(`${origin}${formPath}`);
        if (success) {
            setHasCopied(true);
            window.setTimeout(() => setHasCopied(false), 1600);
        } else {
            setHasCopied(false);
        }
    };

    return (
        <Paper
            variant="outlined"
            sx={{
                mb: 3,
                p: { xs: 2, sm: 2.5 },
                borderRadius: 1,
                borderColor: 'divider',
                bgcolor: 'background.paper',
                boxShadow: (theme) => theme.customShadows.z8
            }}
        >
            <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={2}
                alignItems={{ xs: 'stretch', md: 'center' }}
                justifyContent="space-between"
            >
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
                    <Box
                        sx={{
                            width: 42,
                            height: 42,
                            flexShrink: 0,
                            borderRadius: 1,
                            display: 'grid',
                            placeItems: 'center',
                            color: 'info.main',
                            bgcolor: 'background.neutral'
                        }}
                    >
                        <StorefrontOutlinedIcon />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle1" sx={{ color: 'text.primary' }}>
                            {title}
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                color: 'text.secondary',
                                overflowWrap: 'anywhere'
                            }}
                        >
                            {description}
                        </Typography>
                    </Box>
                </Stack>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems={{ xs: 'stretch', sm: 'center' }}>
                    <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' } }} />
                    <Button
                        variant="outlined"
                        color="inherit"
                        startIcon={<ContentCopyOutlinedIcon />}
                        onClick={() => {
                            void handleCopyLink();
                        }}
                        sx={{ borderColor: 'divider' }}
                    >
                        {hasCopied ? 'Copied' : 'Copy link'}
                    </Button>
                    <Button
                        variant="contained"
                        href={formPath}
                        target="_blank"
                        rel="noopener noreferrer"
                        startIcon={<OpenInNewOutlinedIcon />}
                    >
                        Open form
                    </Button>
                </Stack>
            </Stack>
        </Paper>
    );
}
